import { useNavigate } from 'react-router-dom';
import AppBar from '../components/AppBar';
import { EmptyState, InfiniteSentinel, Skeletons, I } from '../components/kit';
import { usePaginatedList } from '../hooks/usePaginatedList';
import * as memosApi from '../api/memos';
import { memoDate } from '../components/memos/memoFormat';

// More → Memos: every memo HQ has sent this agent, newest first — confirmed ones
// with the time, the rest marked "Not read yet". A memo HQ withdrew is gone.
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' };

// usePaginatedList expects { data: rows, pagination }; this endpoint also returns the unread count.
const fetchPage = async (params) => {
  const r = await memosApi.listMemos(params);
  const d = r?.data ?? r;
  return { data: d?.memos || [], pagination: d?.pagination };
};

export default function MemosPage() {
  const nav = useNavigate();
  const list = usePaginatedList(fetchPage, { limit: 20 });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Memos" sub="from HQ" />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.loading && !list.items.length ? (
          <Skeletons n={4} height={78} />
        ) : !list.items.length ? (
          <EmptyState title="No memos yet" sub="When HQ sends you a memo, it shows here." />
        ) : (
          <>
            <div style={card}>
              {list.items.map((m, i) => {
                const unread = !m.read_at && m.live;
                return (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => nav(`/memos/${m.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') nav(`/memos/${m.id}`); }}
                    style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '14px 16px', borderBottom: i === list.items.length - 1 ? 'none' : '1px solid var(--border)', cursor: 'pointer', background: unread ? 'color-mix(in srgb,var(--primary) 5%,transparent)' : 'transparent' }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{m.from_label} · {memoDate(m.publish_at)}</span>
                        {m.attachment && <span style={{ color: '#cf1322', fontWeight: 700, fontSize: 9.5 }}>PDF</span>}
                        {m.images?.length > 0 && <span style={{ fontSize: 10.5 }}>· {m.images.length} image{m.images.length === 1 ? '' : 's'}</span>}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: unread ? 700 : 600, marginTop: 3, lineHeight: 1.35 }}>{m.title}</div>
                      <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600, color: m.read_at ? 'var(--ok)' : unread ? 'var(--warn)' : 'var(--muted)' }}>
                        {m.read_at ? `✓ You confirmed · ${memoDate(m.read_at)}` : unread ? '● Not read yet — tap to read' : 'No longer showing'}
                      </div>
                    </div>
                    <span style={{ display: 'flex', color: 'var(--muted)', alignSelf: 'center' }}><I.chevR /></span>
                  </div>
                );
              })}
            </div>
            <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
          </>
        )}
      </div>
    </div>
  );
}
